import axios from "axios";
import Cookies from "universal-cookie";
import SessionManager from "./SessionManager";
import moment from "moment";

const cookies = new Cookies();
const RELEASE_NO = "0.0.1";
// const DEFAULT_HOST = "ifillip.com";
// const DEFAULT_HOST = "vivekananda.ifillip.com";
const DEFAULT_HOST = "vallarasuai.ifillip.com";
const TENANT_HEADER = "Tenant-ID";

/* ────────────────────────────────────────────────────────────────
   Multi-tenant host resolution — the app is served from a per-tenant
   subdomain (e.g. abc.ifillip.com), so the API/image bases and the
   tenant header must mirror whatever host the browser is actually on
   instead of pointing at a fixed environment. Localhost, bare IPs,
   the apex domain and "www" have no tenant, so they fall back to
   DEFAULT_HOST as the base host, using DEFAULT_HOST's own first label
   (e.g. "devamhss" out of "devamhss.ifillip.com") as the tenant header
   value — never the full hostname.
──────────────────────────────────────────────────────────────── */
const extractSubdomain = (host) => host.split(".")[0];

const resolveTenant = () => {
  if (typeof window === "undefined" || !window.location?.hostname) {
    return { host: DEFAULT_HOST, subdomain: extractSubdomain(DEFAULT_HOST) };
  }

  const { hostname } = window.location;
  const isIp = /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);
  const parts = hostname.split(".");

  if (
    hostname === "localhost" ||
    isIp ||
    parts.length < 3 ||
    parts[0] === "www"
  ) {
    return { host: DEFAULT_HOST, subdomain: extractSubdomain(DEFAULT_HOST) };
  }

  return { host: hostname, subdomain: parts[0] };
};

const { host: RESOLVED_HOST, subdomain: TENANT_SUBDOMAIN } = resolveTenant();

const API_BASE_URL =
  process.env.REACT_APP_API_URL || `https://${RESOLVED_HOST}/api/V1/api`;

// Image URL Base — derived from the current tenant subdomain.
// When REACT_APP_API_URL points at a local backend (e.g.
// http://localhost:8087/v1/api), images are served by that same backend,
// so the base is derived from it instead of the tenant host.
export const IMAGE_BASE_URL = process.env.REACT_APP_API_URL
  ? `${API_BASE_URL.replace(/\/v1\/api\/?$/i, "")}/`
  : `https://${RESOLVED_HOST}/api/`;

// ADMISSION LOGOS
export const admissionLogo1 = `${IMAGE_BASE_URL}school_logo/profile.jpg`;
export const admissionLogo2 = `${IMAGE_BASE_URL}school_logo/1755065163.png`;

// Cookie options
const COOKIE_OPTIONS = {
  path: "/",
  maxAge: 86400, // 24 hours in seconds
  secure: process.env.NODE_ENV === "production",
  sameSite: "strict",
};

// Token management utilities
export const TokenService = {
  getToken: () => cookies.get("token"),

  setToken: (token) => {
    cookies.set("token", token, COOKIE_OPTIONS);
  },

  removeToken: () => {
    cookies.remove("token", { path: "/" });
  },

  getUser: () => {
    const user = cookies.get("user");
    return user && user.role ? user : null;
  },

  setUser: (user) => {
    cookies.set("user", user, COOKIE_OPTIONS);
  },

  removeUser: () => {
    cookies.remove("user", { path: "/" });
  },

  clearAll: () => {
    cookies.remove("token", { path: "/" });
    cookies.remove("user", { path: "/" });
  },
};

// Create axios instance
export const AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  // headers: {
  //   "Content-Type": "application/json",
  // },
  // timeout: 3000, // 3 seconds timeout
});

// Request interceptor to add auth token
AxiosInstance.interceptors.request.use(
  (config) => {
    const token = TokenService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token} ${RELEASE_NO}`;
    }
    if (TENANT_SUBDOMAIN) {
      config.headers[TENANT_HEADER] = TENANT_SUBDOMAIN;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle errors and show session expired modal
AxiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip interceptor for login requests - let Login component handle errors
    if (originalRequest?.url?.includes("/login/login")) {
      return Promise.reject(error);
    }

    // 426 Upgrade Required → app was updated, force refresh/login
    if (error.response?.status === 426) {
      alert(
        "The application has been updated. Please refresh the page to continue.",
      );
      TokenService.clearAll();
      sessionStorage.removeItem("menu_list");
      window.location.href = "/login";
      return Promise.reject(error);
    }

    // Check if it's a 401 error and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Check if we have a token (user was logged in)
      const token = TokenService.getToken();
      if (token) {
        // Show session expired modal and wait for user action
        try {
          SessionManager.showSessionExpiredModal(originalRequest).catch(
            (modalErr) => {
              console.warn(
                "SessionManager modal promise rejected (handled):",
                modalErr,
              );
            },
          );

          // Return a promise that never resolves (modal will handle it)
          return new Promise(() => {});
        } catch (modalError) {
          console.error("Session modal error:", modalError);
          TokenService.clearAll();
          window.location.href = "/login";
          return Promise.reject(error);
        }
      } else {
        // No token, redirect to login
        TokenService.clearAll();
        window.location.href = "/login";
        return Promise.reject(error);
      }
    }

    // If it's a 401 after retry (refresh failed), clear tokens and redirect
    if (error.response?.status === 401 && originalRequest._retry) {
      TokenService.clearAll();
      SessionManager.handleSessionInvalid();
      window.location.href = "/login";
    }

    return Promise.reject(error);
  },
);

// Auth API - Updated endpoints based on API documentation
export const authAPI = {
  // POST /api/login/login
  login: (credentials) =>
    AxiosInstance.post("/login/login", {
      email: credentials.email,
      password: credentials.password,
    }),

  // GET /api/login/profile
  getProfile: () => AxiosInstance.get("/login/profile"),

  // POST /api/login/refresh-token
  refreshToken: () => {
    const token = TokenService.getToken();
    const user = TokenService.getUser();

    const payload = {
      user_id: user?.id || null,
      email: user?.email || null,
      role: user?.role?.id || null,
    };

    return axios.post(`${API_BASE_URL}/login/refresh-token`, payload, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token} ${RELEASE_NO}`,
        ...(TENANT_SUBDOMAIN ? { [TENANT_HEADER]: TENANT_SUBDOMAIN } : {}),
      },
    });
  },

  // POST /api/login/logout
  // Send user_id + token as payload on every logout trigger
  logout: () => {
    const token = TokenService.getToken();
    const user = TokenService.getUser();

    const payload = {
      user_id: user?.id || null,
      token: token || null,
    };

    return AxiosInstance.post("/login/logout", payload);
  },
};

export const toUtcIso = (date) =>
  date
    ? moment(date).startOf("day").utc().format("YYYY-MM-DD[T]HH:mm:ss.SSS[Z]")
    : null;

export const toLocalDate = (utcDate) =>
  utcDate ? moment.utc(utcDate).local().format("YYYY-MM-DD") : null;

/* ────────────────────────────────────────────────────────────────
   Name-field validation — shared by every "Name" style text input.

   Allowed: letters of any script (\p{L} + \p{M} keeps Tamil vowel
   signs intact), digits, spaces, hyphen and period.
   Rejected: @ # $ % ^ ! & * ( ) etc. and every emoji — symbols fall
   outside the whitelist, so "@Fee#Type!" and "Fee😊" are stripped.
──────────────────────────────────────────────────────────────── */
const NAME_DISALLOWED_REGEX = /[^\p{L}\p{M}\p{N} .-]/gu;

/* Variation selector, zero-width joiner and the enclosing keycap — the
   glue inside an emoji sequence. Dropped first so a keycap emoji cannot
   leave its bare digit behind. */
const EMOJI_JOINER_REGEX = new RegExp("[\\uFE0F\\u200D\\u20E3]", "g");

/** Strip every disallowed character. Use on onChange to block them as typed. */
export const sanitizeNameInput = (value = "") =>
  String(value ?? "")
    .replace(EMOJI_JOINER_REGEX, "")
    .replace(NAME_DISALLOWED_REGEX, "");

/** True when the value carries a special character or an emoji. */
export const hasInvalidNameChars = (value = "") =>
  sanitizeNameInput(value) !== String(value ?? "");

export default AxiosInstance;
